import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
    GET_REPORT_DETAILS,
    GetReportDetailsData,
    GetReportDetailsVariables,
    TOGGLE_REPORT_CASE_REVIEW_STATUS,
    ToggleReportCaseReviewStatusData,
    ToggleReportCaseReviewStatusVariables
} from '@jwp/ott-common/src/graphql/query/reportModeration.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { PATH_CHANNEL_REPORTS } from '@jwp/ott-common/src/paths';
import CheckIcon from '@jwp/ott-theme/assets/icons/check.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import DeleteChannelIcon from '@jwp/ott-theme/assets/icons/Delete_icon.svg?react';
import SuspendChannelIcon from '@jwp/ott-theme/assets/icons/CreateChannelIcon.svg?react';
import RemovecontentIcon from '@jwp/ott-theme/assets/icons/Removecontent.svg?react';
import AlertChannelIcon from '@jwp/ott-theme/assets/icons/AlertChannel.svg?react';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import BlockIcon from '@jwp/ott-theme/assets/icons/Block.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play_white.svg?react';
import PauseIcon from '@jwp/ott-theme/assets/icons/pause.svg?react';
import VolumeUpIcon from '@jwp/ott-theme/assets/icons/audio-on.svg?react';
import VolumeOffIcon from '@jwp/ott-theme/assets/icons/audio-off.svg?react';
import FullscreenIcon from '@jwp/ott-theme/assets/icons/player_Full Screen.svg?react';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import ReportReasonBreakdown from './ReportReasonBreakdown';
import IconButton from '../../components/IconButton/IconButton';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Spinner from '../../components/Spinner/Spinner';
import { formatDateMMMddYYYY } from '@jwp/ott-common/src/utils/datetime';
import styles from './ReportDetails.module.scss';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Dialog from '../../components/Dialog/Dialog';
import Alert from '../../components/Alert/Alert';
import ReportersList from './ReportersList';
import ActionDetailsModal from '../ReportsModal/ActionDetailsModal';
import env from '@jwp/ott-common/src/env';
import { channelURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';
import TakeActionModal from '../ReportsModal/TakeActionModal';

// Row Helper
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className={styles.detailItem}>
        <span className={styles.detailLabel}>{label}</span>
        <span className={styles.detailValue}>{children}</span>
    </div>
);

// Format remaining time like "-00:54:39" or "-00:00"
const formatRemainingTime = (currentTime: number, duration: number) => {
    const remaining = Math.max(0, duration - currentTime);
    if (isNaN(remaining) || !isFinite(remaining)) return '-00:00';

    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = Math.floor(remaining % 60);

    const pad = (num: number) => num.toString().padStart(2, '0');

    if (hours > 0) {
        return `-${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `-${pad(minutes)}:${pad(seconds)}`;
};

const ReportDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation(['user', 'video', 'common', 'upload']);
    const navigate = useNavigate();
    const location = useLocation();


    const DEFAULT_CHANNEL_AVATAR = '/static/default-avatar.png';

    const actionMenuRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerWrapRef = useRef<HTMLDivElement>(null);

    const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [TakeActionModalOpen, setTakeActionModalOpen] = useState(false)
    const [takeActionModalType, setTakeActionModalType] = useState<string>('');
    const [successOpen, setSuccessOpen] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Custom Video Player States
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [duration, setDuration] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [isBuffering, setIsBuffering] = useState<boolean>(false);
    const [hasVideoError, setHasVideoError] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    const user = useAccountStore((state) => state.user);
    const canDelete = hasPermission(user, PERMISSIONS.DELETE_BROADCAST_LIVE);

    const contentTypeMap: Record<string, string> = {
        REEL: 'Reel',
        CHANNEL: 'Channel',
    };

    const [toggleReviewStatus] = useMutation<
        ToggleReportCaseReviewStatusData,
        ToggleReportCaseReviewStatusVariables
    >(TOGGLE_REPORT_CASE_REVIEW_STATUS, {
        client: getAuthApolloClient(),
    });

    const {
        data: reportDetailsData,
        loading,
        error,
        refetch,
    } = useQuery<GetReportDetailsData, GetReportDetailsVariables>(GET_REPORT_DETAILS, {
        client: getAuthApolloClient(),
        variables: { rcId: id || '' },
        skip: !id,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    });

    const reportDetails = reportDetailsData?.getReportDetails;
    const isReel = reportDetails?.media?.maType === 'reels';
    const isContentRemoved = reportDetails?.reportableType === 'CHANNEL';


    const handleMarkInReview = async () => {
        try {
            const { data } = await toggleReviewStatus({
                variables: { rcId: id || '' },
            });

            if (data?.toggleReportCaseReviewStatus) {
                // refetch();
                showToast(
                    t('common:report_submitted', {
                        defaultValue: 'Marked successfully',
                    }),
                    'success'
                );

                navigate(`${PATH_CHANNEL_REPORTS}?tab=in_review`)
            }
        } catch (error) {
            console.error('Error toggling review status:', error);
            setErrorMessage('Failed to update review status');
            setErrorOpen(true);
        }
    };

    useEffect(() => {
        if (!location.search) {
            refetch();
        }
    }, [location.search, refetch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (actionMenuRef.current && !actionMenuRef.current.contains(target)) {
                setUploadMenuOpen(false);
            }
        };

        if (uploadMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [uploadMenuOpen]);


    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const thumbnailUrl = useMemo(
        () => `${env.APP_BASE_URL || ''}${reportDetails?.media?.maS3ThumbnailKeyUrl || ''}`,
        [reportDetails?.media?.maS3ThumbnailKeyUrl]
    );

    const channelAvatarlUrl = useMemo(
        () =>
            reportDetails?.channel?.chAvatarUrl
                ? `${env.APP_BASE_URL || ''}${reportDetails.channel.chAvatarUrl}`
                : DEFAULT_CHANNEL_AVATAR,
        [reportDetails?.channel?.chAvatarUrl]
    );

    const videoSrc = useMemo(() => {
        const media = reportDetails?.media as Record<string, any> | undefined;
        if (!media) return '';

        let rawPath =
            media.maS3MediaKeyUrl ||
            media.maUrl ||
            media.maS3KeyUrl ||
            media.videoUrl ||
            media.mediaUrl ||
            '';

        if (!rawPath && media.maS3ThumbnailKeyUrl) {
            rawPath = media.maS3ThumbnailKeyUrl
                .replace('/thumbnail/', '/')
                .replace(/\.(jpg|jpeg|png|webp)$/i, '.mp4');
        }

        if (!rawPath) return '';

        if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
            return rawPath;
        }

        const baseUrl = (env.APP_BASE_URL || '').replace(/\/$/, '');
        const cleanPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

        return `${baseUrl}${cleanPath}`;
    }, [reportDetails?.media]);

    useEffect(() => {
        if (videoSrc) {
            setHasVideoError(false);
            setIsBuffering(false);
        } else if (reportDetails && !videoSrc) {
            setHasVideoError(true);
            setIsBuffering(false);
        }
    }, [videoSrc, reportDetails]);

    // Player Controls
    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                const promise = videoRef.current.play();
                if (promise !== undefined) {
                    promise.catch((err) => {
                        console.error('Playback Error:', err);
                        setIsPlaying(false);
                    });
                }
            }
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            const newMuted = !isMuted;
            videoRef.current.muted = newMuted;
            setIsMuted(newMuted);
        }
    };

    const toggleFullscreen = () => {
        if (!playerWrapRef.current) return;
        if (!document.fullscreenElement) {
            playerWrapRef.current.requestFullscreen().catch((err) => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = Number.parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const handleDeleteStream = async () => {
        if (!reportDetails) return;
    };

    const handleDeleteSuccessClose = () => {
        setSuccessOpen(false);
        navigate(`${PATH_CHANNEL_REPORTS}?tab=reports`, { replace: true });
    };

    const handleViewChannel = () => {
        if (reportDetails?.channel?.chId) {
            const url = channelURL(reportDetails?.channel?.chId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            showToast(t('channel.not_found_title'), 'error');
        }
    };

    const takeActionHandler = (type: string) => {
        setTakeActionModalType(type)
        setTakeActionModalOpen(true)
    }


    const renderResolvedOverlay = () => {
        if (reportDetails?.status !== 'RESOLVED') return null;

        const removedDate =
            (reportDetails as Record<string, any>)?.resolvedAt ||
            reportDetails?.lastReportedAt;

        return (
            <div className={styles.resolvedOverlay}>
                <div
                    className={styles.resolvedBgBlur}
                // style={{ backgroundImage: `url(${thumbnailUrl || channelAvatarlUrl})` }}
                />
                <div className={styles.resolvedDarkOverlay} />
                <div className={styles.resolvedContent}>
                    <div className={styles.resolvedIconCircle}>
                        <Icon icon={BlockIcon} />
                    </div>
                    <h3 className={styles.resolvedTitle}>
                        {reportDetails?.action?.actionLabel}
                    </h3>
                    <p className={styles.resolvedDescription}>
                        This content was removed on {formatDateMMMddYYYY(reportDetails?.action?.takenOn)} for violating community guidelines
                    </p>
                </div>
            </div>
        );
    };



    const renderCenter = () => {
        if (isContentRemoved) {
            return (
                <div className={styles.blurredBgWrapper}>
                    <div
                        className={styles.bgBlurLayer}
                        style={{ backgroundImage: `url(${channelAvatarlUrl})` }}
                    />
                    <div className={styles.darkOverlay} />
                    <img
                        src={channelAvatarlUrl}
                        alt="Channel Profile"
                        className={styles.centerAvatar}
                    />
                </div>
            );
        }

        return (
            <div className={styles.playerWrap} ref={playerWrapRef}>
                {videoSrc && !hasVideoError ? (
                    <div
                        className={classNames(styles.videoWrapper, {
                            [styles.reelVideoWrapper]: isReel,
                            [styles.fullscreenReel]: isReel && isFullscreen,
                        })}
                        onClick={togglePlay}
                    >
                        <video
                            ref={videoRef}
                            src={videoSrc}
                            className={classNames(styles.video, {
                                [styles.reelVideo]: isReel,
                            })}
                            poster={thumbnailUrl}
                            muted={isMuted}
                            crossOrigin="anonymous"
                            onTimeUpdate={() =>
                                videoRef.current && setCurrentTime(videoRef.current.currentTime)
                            }
                            onLoadedMetadata={() => {
                                if (videoRef.current) {
                                    setDuration(videoRef.current.duration);
                                }
                            }}
                            onEnded={() => {
                                setIsPlaying(false);
                                setIsBuffering(false);
                            }}
                            // Video play aagumbodhu wait/buffering aana mattum loader show aagum
                            onWaiting={() => setIsBuffering(true)}
                            onPlaying={() => {
                                setIsBuffering(false);
                                setIsPlaying(true);
                            }}
                            onPause={() => {
                                setIsPlaying(false);
                                setIsBuffering(false);
                            }}
                            onError={(e) => {
                                console.error('Video Load Error path:', videoSrc, e);
                                setIsBuffering(false);
                                setHasVideoError(true);
                            }}
                            playsInline
                        />

                        {/* Buffering Spinner */}
                        {isBuffering && (
                            <div className={styles.loadingOverlay}>
                                <Spinner size="medium" />
                            </div>
                        )}

                        {/* Center Play Icon Overlay */}
                        {!isPlaying && !isBuffering && (
                            <div className={styles.playOverlay}>
                                <Icon icon={PlayIcon} className={styles.largePlayIcon} />
                            </div>
                        )}

                        {/* Top Right Controls */}
                        <div className={styles.topRightControls} onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                onClick={toggleMute}
                                className={styles.controlIconBtn}
                                aria-label="Mute Toggle"
                            >
                                <Icon icon={isMuted ? VolumeOffIcon : VolumeUpIcon} />
                            </button>
                            <button
                                type="button"
                                onClick={toggleFullscreen}
                                className={styles.controlIconBtn}
                                aria-label="Fullscreen Toggle"
                            >
                                <Icon icon={FullscreenIcon} />
                            </button>
                        </div>

                        {/* Bottom Full-width Seek Bar & Countdown Timer */}
                        <div className={styles.bottomControls} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.timerText}>
                                {formatRemainingTime(currentTime, duration)}
                            </div>
                            <div className={styles.progressBarContainer}>
                                <input
                                    type="range"
                                    className={styles.scrubber}
                                    style={
                                        {
                                            '--percent': `${(currentTime / (duration || 1)) * 100}%`,
                                        } as React.CSSProperties
                                    }
                                    min={0}
                                    max={duration || 0}
                                    step={0.1}
                                    value={currentTime}
                                    onChange={handleSeek}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.previewCenter}>
                        <div className={styles.iconCircle}>
                            <Icon icon={BlockIcon} className={styles.iconCircle} />
                        </div>
                        <h3>
                            {hasVideoError
                                ? t('user:report_details.video_error', { defaultValue: 'Failed to load video' })
                                : t('user:report_details.no_video_source', { defaultValue: 'No Video Available' })}
                        </h3>
                    </div>
                )}
            </div>
        );
    };

    const renderFooter = () => {
        if (isContentRemoved) {
            return (
                <div className={styles.cardFooterRemoved}>
                    <div className={styles.channelHeaderRow}>
                        <img
                            src={channelAvatarlUrl}
                            alt={reportDetails?.channel?.chName}
                            className={styles.channelAvatar}
                        />
                        <div className={styles.channelMeta}>
                            <div className={styles.channelName}>{reportDetails?.channel?.chName}</div>
                            <div className={styles.statsRow}>
                                <span>{reportDetails?.channel?.chHandle}</span>
                                <span className={styles.bullet}>|</span>
                                <span>{reportDetails?.channel?.followersCount} {t('user:report_details.followers')}</span>
                                <span className={styles.bullet}>|</span>
                                <span>{reportDetails?.channel?.followingCount} {t('user:report_details.following')}</span>
                                <span className={styles.bullet}>|</span>
                                <span>{reportDetails?.channel?.videosCount} {t('user:report_details.videos')}</span>
                            </div>
                        </div>
                        <button className={styles.viewChannelBtn}
                            onClick={() => {
                                handleViewChannel()
                            }}
                        >{t('user:report_details.view_channel')}</button>
                    </div>

                    <p className={styles.description}>{reportDetails?.channel?.chDescription}</p>
                </div>
            );
        }

        return (
            <div className={styles.previewFooter}>
                <h2 className={styles.cardTitle}>{reportDetails?.media?.maTitle}</h2>
                <p className={styles.descriptionText}>{reportDetails?.media?.maDescription}</p>

                <div className={styles.divider} />

                <div className={styles.channelRow}>
                    <img
                        src={channelAvatarlUrl}
                        alt={reportDetails?.channel?.chName}
                        className={styles.channelAvatar}
                    />
                    <div className={styles.channelInfo}>
                        <div className={styles.channelName}>{reportDetails?.channel?.chName}</div>
                        <div className={styles.channelStats}>
                            <span>{reportDetails?.channel?.chHandle}</span>
                            <span className={styles.bullet}>|</span>
                            <span>{reportDetails?.channel?.followersCount} {t('user:report_details.followers')}</span>
                            <span className={styles.bullet}>|</span>
                            <span>{reportDetails?.channel?.followingCount} {t('user:report_details.following')}</span>
                            <span className={styles.bullet}>|</span>
                            <span>{reportDetails?.channel?.videosCount} {t('user:report_details.videos')}</span>
                        </div>
                    </div>
                    <button className={styles.viewChannelBtn}
                        onClick={() => {
                            handleViewChannel()
                        }}>{t('user:report_details.view_channel')}</button>
                </div>
            </div>
        );
    };

    if (loading) {
        return <LoadingOverlay />;
    }
    if (error || !reportDetails) {
        return <div className={styles.errorState}>{t('video:broadcast_live.load_error')}</div>;
    }

    const handleBack = () => {
        const searchParams = new URLSearchParams(location.search);
        const returnPath = searchParams.get('returnPath');

        if (returnPath) {
            navigate(returnPath);
        } else {
            navigate(`${PATH_CHANNEL_REPORTS}?tab=reports`);
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header} ref={actionMenuRef}>
                <div className={styles.headerLeft}>
                    <IconButton
                        className={styles.iconButton}
                        onClick={handleBack}
                    >
                        <img src={BackwardArrow} alt={t('common:back')} />
                    </IconButton>
                    <h1>{t('user:report_details.title')}</h1>
                </div>
                {reportDetails?.status === 'RESOLVED' ?
                    <Button
                        label={t('user:report_details.view_action_details')}
                        variant="outlined"
                        className={styles.editButton}
                        onClick={() => setActionModalOpen(true)}
                    /> :
                    <>
                        {reportDetails?.status === 'OPEN' ? (
                            <Button
                                label={t('user:report_details.mark_as_in_review_btn')}
                                variant="outlined"
                                className={styles.editButton}
                                onClick={() => handleMarkInReview()}
                            />

                        ) : (
                            <Button
                                label={t('user:report_details.mark_as_in_review_btn')}
                                startIcon={<Icon icon={CheckIcon} />}
                                variant="outlined"
                                className={styles.editButton}
                                disabled
                            />
                        )}
                        <div className={styles.actionMenuWrapper}>
                            <button
                                type="button"
                                className={styles.uploadButton}
                                onClick={() => setUploadMenuOpen((prev) => !prev)}
                            >
                                {t('user:report_details.take_action')}
                                <ChevronRight className={styles.expandIcon} aria-hidden="true" />
                            </button>

                            {uploadMenuOpen && (
                                <div className={styles.uploadMenu}>
                                    <button type="button" className={styles.uploadMenuItem} onClick={() => takeActionHandler('warn_creator')}>
                                        <AlertChannelIcon className={styles.uploadMenuIcon} aria-hidden="true" />
                                        {t('user:report_details.warn_creator')}
                                    </button>
                                    <div className={styles.divider} />
                                    <button type="button" className={styles.uploadMenuItem} onClick={() => takeActionHandler('remove_content')}>
                                        <RemovecontentIcon className={styles.uploadMenuIcon} aria-hidden="true" />
                                        {t('user:report_details.remove_content')}
                                    </button>
                                    <div className={styles.divider} />
                                    <button type="button" className={styles.uploadMenuItem} onClick={() => takeActionHandler('suspend_channel')}>
                                        <SuspendChannelIcon className={styles.uploadMenuIcon} aria-hidden="true" />
                                        {t('user:report_details.suspend_channel')}
                                    </button>
                                    <div className={styles.divider} />
                                    <button type="button" className={styles.dropdownItemDanger} onClick={() => takeActionHandler('delete_channel')}>
                                        <Icon icon={DeleteChannelIcon} className={styles.deleteMenuIcon} />
                                        {t('channel.delete.menu_label')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                }
            </header>

            <div className={styles.content}>
                {/* LEFT COLUMN */}
                <div className={styles.leftColumn}>
                    <div className={classNames(styles.previewBox)}>
                        <div
                            className={classNames(
                                isContentRemoved ? styles.previewBodyChannel : styles.previewBody
                            )}
                        >
                            {renderCenter()}
                            {renderResolvedOverlay()}
                        </div>
                        {renderFooter()}
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className={styles.rightColumn}>
                    {/* Report Summary */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t('user:report_details.reports_summary')}</h2>
                        <div className={styles.tripleRow}>
                            <Row label={t('user:reports.report_filter.content_type')}>
                                {contentTypeMap[reportDetails?.reportableType ?? ''] ?? 'Video'}
                            </Row>
                            <Row label={t('user:report_details.reports_count')}>
                                {reportDetails?.reportsCount}
                            </Row>
                            <Row label={t('user:report_details.current_status')}>
                                {reportDetails?.status === 'RESOLVED' ? (
                                    <span className={styles.badgeReviewed}>Reviewed</span>
                                ) : (
                                    <span className={styles.badgeInReview}>In Review</span>
                                )}
                            </Row>
                        </div>
                        <div className={styles.tripleRow}>
                            <Row label={t('user:report_details.first_reported_on')}>
                                {formatDateMMMddYYYY(reportDetails?.firstReportedAt)}
                            </Row>
                            <Row label={t('user:report_details.last_reported_on')}>
                                {formatDateMMMddYYYY(reportDetails?.lastReportedAt)}
                            </Row>
                            <Row label={t('user:report_details.top_reported_category')}>
                                {reportDetails?.topReportedCategory}
                            </Row>
                        </div>
                    </div>

                    {/* Report Reason Breakdown */}
                    <ReportReasonBreakdown data={reportDetails?.reasonBreakdown} />
                </div>
            </div>

            <ReportersList />
            {actionModalOpen &&
                <ActionDetailsModal
                    onClose={() => setActionModalOpen(false)}
                    details={reportDetails?.action}
                />
            }

            {TakeActionModalOpen &&
                <TakeActionModal
                    popupType={takeActionModalType}
                    onClose={() => setTakeActionModalOpen(false)}
                    rcId={id}
                />
            }


            <ConfirmationDialog
                open={confirmationOpen}
                variant="delete"
                title={t('video:broadcast_live.delete_title')}
                body={t('video:broadcast_live.delete_body')}
                confirmLabel={t('video:broadcast_live.delete_confirm')}
                closeLabel={t('common:no')}
                onConfirm={handleDeleteStream}
                onClose={() => setConfirmationOpen(false)}
                busy={deleteLoading}
            />

            <Dialog open={successOpen} onClose={handleDeleteSuccessClose} role="alertdialog">
                <div className={styles.successDialog}>
                    <div className={styles.successIconContainer}>
                        <Icon icon={CheckIcon} />
                    </div>
                    <h2>{t('video:broadcast_live.delete_success_title')}</h2>
                    <p>{t('video:broadcast_live.delete_success_description')}</p>
                    <Button
                        label={t('common:okay')}
                        variant="contained"
                        color="primary"
                        onClick={handleDeleteSuccessClose}
                        fullWidth
                    />
                </div>
            </Dialog>

            <Alert open={errorOpen} message={errorMessage} onClose={() => setErrorOpen(false)} />
        </div>
    );
};

export default ReportDetails;